import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { BodyCharacteristicForm } from "@corpuscomplete/core-ui/body/body-characteristic/form";
import { DateOfBirthInput } from "@corpuscomplete/web/components/body/date-of-birth-input";
import { Page, PageBody, PageContent } from "@corpuscomplete/web/components/layout";
import { FormErrors } from "@corpuscomplete/web/hooks/create-form-hook";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
} from "@corpuscomplete/web-ui/components/item";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@corpuscomplete/web-ui/components/radio-group";
import { Text } from "@corpuscomplete/web-ui/components/text";

export function SettingsPage() {
  return (
    <Page>
      <PageBody>
        <PageContent className="container mx-auto max-w-2xl py-10">
          <Text variant="h3" className="mb-8">
            Settings
          </Text>

          <BodyCharacteristicForm.DataProvider>
            <BodyCharacteristicForm.FormProvider>
              <BodyCharacteristicsSection />
            </BodyCharacteristicForm.FormProvider>
          </BodyCharacteristicForm.DataProvider>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function BodyCharacteristicsSection() {
  const isSubmitEnabled = BodyCharacteristicForm.useIsSubmitEnabled();
  const onSubmit = BodyCharacteristicForm.useOnSubmit();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <Text variant="h5">Body Characteristics</Text>
        {isSubmitEnabled ? (
          <Button onClick={onSubmit} size="sm">
            Save
          </Button>
        ) : null}
      </div>
      <ItemGroup>
        <BodyCharacteristicFormContent />
      </ItemGroup>
    </div>
  );
}

function BodyCharacteristicFormContent() {
  const { isLoading } = BodyCharacteristicForm.useDataContext();
  const form = BodyCharacteristicForm.useFormContext();

  if (isLoading) {
    return null;
  }

  return (
    <form.AppForm>
      <form.AppField name="dateOfBirth">
        {(field) => (
          <Item size="sm">
            <ItemContent>
              <ItemTitle>Date of Birth</ItemTitle>
              <ItemDescription>Used for nutrition program calculations.</ItemDescription>
            </ItemContent>
            <ItemActions>
              <DateOfBirthInput
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={field.handleChange}
                errors={field.state.meta.errors}
              />
            </ItemActions>
          </Item>
        )}
      </form.AppField>

      <ItemSeparator />

      <form.AppField name="biologicalSex">
        {(field) => (
          <Item size="sm">
            <ItemContent>
              <ItemTitle>Biological Sex</ItemTitle>
              <ItemDescription>Used for nutrition program calculations.</ItemDescription>
            </ItemContent>
            <ItemActions>
              <RadioGroup
                className="flex flex-row gap-4"
                value={field.state.value ?? ""}
                onValueChange={(value) =>
                  field.handleChange(value as BodyCharacteristic.BiologicalSex)
                }
              >
                {BodyCharacteristic.biologicalSexOptions.map((option) => (
                  <div key={option.value} className="flex items-center gap-2">
                    <RadioGroupItem id={option.value} value={option.value} />
                    <Label htmlFor={option.value} className="text-xs">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </ItemActions>
          </Item>
        )}
      </form.AppField>

      <form.Subscribe selector={(state) => state.errors}>
        {(errors) =>
          errors.length > 0 && (
            <div className="px-4 py-2">
              <FormErrors errors={errors} />
            </div>
          )
        }
      </form.Subscribe>
    </form.AppForm>
  );
}
